/**
 * Zod schema for the `rag_search` workflow node configuration.
 *
 * Sprint 0 — defined in @argusai/shared so both the workflow builder
 * (validation at save time) and the automate-runner (validation at
 * execute time) share the same source of truth. CLAUDE.md Rule 26:
 * Zod schemas are the source of truth for all types.
 *
 * v1.1 §13 canonical shape:
 *   query_template, collection, top_k, apply_reranker, apply_crag_gate
 *
 * DECISION 29 (B8, Library LB4-T4): the node has NO visibility switches. It
 * always applies THE Document visibility rule (policy/document-visibility.ts,
 * surface 'retrieval') — classification, shares, departments, drafts — and the
 * author cannot turn any of it off. The two author switches the node once
 * carried are gone from this schema, the builder and the runner. A workflow
 * saved before the change still carries them in its stored definition: the
 * schema STRIPS keys it does not declare (it is not `.strict()`), so a stored
 * value is ignored — never honoured, and never a parse failure that would
 * stop an existing workflow from running.
 */

import { z } from 'zod';

/**
 * The `collection` field accepts either:
 *   1. A well-known alias:  "tenant.knowledge_base"
 *   2. A verbatim Qdrant collection name (advanced, power_user only)
 *
 * The alias is resolved by resolveCollectionAlias() in @argusai/rag
 * at runtime using the executing tenant's organizationId.
 */
export const RagSearchNodeConfigSchema = z
  .object({
    /**
     * The search query. Supports {{stepId.field}} template substitution
     * using prior step outputs (resolved by the runner before the node
     * executes). A static string or a template. Required.
     *
     * Max 2000 chars to prevent embedding a full document as the query.
     */
    query_template: z.string().min(1).max(2000),

    /**
     * Qdrant collection name or alias. The special alias
     * "tenant.knowledge_base" resolves to the tenant's canonical
     * document collection (tenant_{tenantId_no_dashes}_docs).
     *
     * Other values are treated as verbatim collection names — advanced
     * usage for tenants with multiple named collections.
     */
    collection: z.string().min(1).default('tenant.knowledge_base'),

    /**
     * Number of chunks to return after RRF (and optionally after
     * reranking). The reranker may reduce this further if fewer chunks
     * have sufficient signal.
     *
     * Default 10 mirrors the RRF_TOP_K used in the Ask Argus pipeline.
     * Max 50 prevents over-long step outputs bloating
     * workflow_step_executions.output (1 MB cap per Sprint 1 schema).
     */
    top_k: z.number().int().min(1).max(50).default(10),

    /**
     * When true (default), sends the RRF-fused candidates through the
     * BGE-reranker-v2-m3 ONNX sidecar. Falls back gracefully if the
     * reranker is unavailable (passthrough, no error).
     *
     * Set false to skip reranking in latency-sensitive automated
     * workflows where ranking quality is less important than speed.
     */
    apply_reranker: z.boolean().default(true),

    /**
     * When true, applies CRAG quality assessment before returning results.
     * If quality == 'low' and action == 'admit_ignorance', the node
     * returns an empty array with a crag_gate_triggered flag instead of
     * the low-confidence chunks.
     *
     * Default false for V1 (opt-in). Will default true in V2 once the
     * UX for "workflow got no relevant documents" is built.
     */
    apply_crag_gate: z.boolean().default(false),
  })
  // Undeclared keys are STRIPPED, not refused: a definition saved with the
  // retired visibility switches (decision 29) still runs, and the stored
  // values are dropped here, before the runner sees the config.
  .strip();

export type RagSearchNodeConfig = z.infer<typeof RagSearchNodeConfigSchema>;

/**
 * Output shape returned by execRagSearch — an array of RankedResult-aligned
 * objects. This IS the workflow step output persisted to
 * workflow_step_executions.output and available as {{stepId.chunks}}
 * to downstream nodes.
 *
 * Shape is intentionally identical to @argusai/rag's RankedResult so
 * downstream agent nodes can consume the chunks without format translation.
 * We re-declare it here (not import) to avoid @argusai/shared depending
 * on @argusai/rag.
 */
export const RagSearchResultItemSchema = z.object({
  chunkId: z.string(),
  text: z.string(),
  rrfScore: z.number(),
  metadata: z.record(z.string(), z.unknown()),
  denseScore: z.number().optional(),
  sparseScore: z.number().optional(),
});

export type RagSearchResultItem = z.infer<typeof RagSearchResultItemSchema>;

/**
 * The full output of a rag_search node step.
 *
 * Downstream nodes access:
 *   {{stepId.chunks}}                  — the array of results
 *   {{stepId.chunks.0.text}}           — first chunk text (via template resolver)
 *   {{stepId.collection}}              — resolved Qdrant collection name
 *   {{stepId.query}}                   — the resolved query (after template substitution)
 *   {{stepId.crag_quality}}            — "high" | "medium" | "low" | "not_assessed"
 *   {{stepId.crag_gate_triggered}}     — true if CRAG blocked results
 *   {{stepId.result_count}}            — length of chunks array
 */
export const RagSearchOutputSchema = z.object({
  chunks: z.array(RagSearchResultItemSchema),
  collection: z.string(),
  query: z.string(),
  result_count: z.number().int().nonnegative(),
  crag_quality: z.enum(['high', 'medium', 'low', 'not_assessed']),
  crag_gate_triggered: z.boolean(),
  reranker_applied: z.boolean(),
});

export type RagSearchOutput = z.infer<typeof RagSearchOutputSchema>;
